import { Box, Button, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  FileDecision,
  FormField,
  FormSection,
  OptionSource,
  PersonaStep,
  StepComment,
  StepDetail,
  StepGlobal,
  StepUpload,
  NoteAttachmentContext,
} from "../dtos";
import { useStepMutations } from "../hooks/use-step-mutations.hook";
import DocumentField from "./document-field";
import { destructiveButtonSx, primaryButtonSx, secondaryButtonSx } from "./field-shell";
import { normalizePhone } from "../utils/phone-countries";
import FormFieldInput from "./form-fields";
import StepWidget from "./finance-widgets";
import { paymentPlanInputKeys } from "../utils/payment-plan-input-keys";
import CreditFeedbackCards, {
  creditPathConfirm,
  isCreditFeedbackOptions,
} from "./credit-feedback-cards";
import {
  actionNeedsComment,
  actionUsesInvokeEndpoint,
  computeLiveGates,
  evaluatePreconditionState,
  preconditionSupported,
} from "../utils/conditions";
import { calculatedKeys, evaluateDerivedFills } from "../utils/derive";
import { blockersTooltip, computeStepBlockers } from "../utils/step-blockers";
import { formatThousands } from "../utils/format-number";
import { fieldEntriesOf, fieldRuleErrors, ruleFactorFills } from "../utils/validate";
import { AppealAnchor, firstFilled } from "../utils/appeals";
import AppealsProvider from "./appeals-provider";
import InlineAppeal from "./appeal-thread-inline";
import CommentsPanel from "./comments-panel";
import DecisionPanel from "./decision-panel";
import { useOptionSources } from "../hooks/use-option-sources.hook";
import ActionConfirmModal, { ActionConfirmRequest } from "./action-confirm-modal";
import DbrSummaryModal from "./dbr-summary-modal";
import {NoteAttachmentList} from "./note-attachments";
import {StepHistoryPanel} from "./context-widgets";
import {shouldHideLegacyHistoryWidget, stepHistoryView} from "../utils/step-history-config";

/** §W3.2: the derived approved amount the accept-approved-amount card displays. */
const DBR_MAX_REF = "dbr.maxLoanForChosenTenor";

/** Submission key convention: the field's authored key `k` (the ENGINE's lookup key — on the
 * newest KYC template `k` is "Identity.Full Name" while the section is "Personal Data" with Arabic
 * labels, so section+label writes keys the completion check never reads), else the global ref,
 * else "<section name>.<field label>" for legacy schemas that carry no `k`. */
/** §Late-docs-note: a document-anchored note (`fieldKey` set) renders beside its document card, never in the
 * step thread or the send-back panel — WM's `conversationOf` rule, mirrored. */
const conversationOf = (comments: StepComment[] | undefined): StepComment[] =>
  (comments ?? []).filter((comment) => comment.fieldKey == null);

const fieldKeyOf = (section: FormSection, field: FormField): string =>
  field.k ?? field.ref ?? `${section.name}.${field.l}`;

/**
 * Flat submission key of the client's mobile on the Client Profile step. EVERY save
 * whose data carries a non-empty value under this key detours through crm-backend for
 * telephone reconciliation (idempotent there: already-linked numbers pass straight
 * through) — no client-side change detection, so the check can never be skipped.
 * Other tel fields (e.g. treasury payment mobiles) are not the client's number.
 */
const CLIENT_MOBILE_KEY = "Client Info.Mobile Number";
/** §Autosave: idle time after a field's LAST edit before that field's timer flushes the draft. */
const AUTOSAVE_DEBOUNCE_MS = 1000;

const isDocumentField = (field: FormField): boolean =>
  field.t === "document" || field.t === "file";

/* ------------------------------------------------------------------------------------------------
 * Pure per-field helpers, lifted out of the render map so the map callback stays readable (and under
 * the complexity budget). Each takes exactly what it reads — no closures over component state.
 * ---------------------------------------------------------------------------------------------- */

/** §Mirrors: the source-field presentation behind a qualified `@s<order>:` reference, when one resolves. */
const upstreamFieldOf = (
  detail: StepDetail,
  field: FormField
): { label: string; type: string; options: string[] | null; editable: boolean } | undefined =>
  field.ref?.startsWith("@s") ? detail.upstreamFields?.[field.ref] : undefined;

/** §Editable-mirrors: the server's `editable` is the authority; schema `mode` covers older payloads. */
const isMirrorEditable = (
  upstreamField: ReturnType<typeof upstreamFieldOf>,
  field: FormField
): boolean => upstreamField?.editable ?? field.mode === "edit";

/**
 * §Placements + §Editable-mirrors: enrich a raw {ref, mode} schema field with its resolved presentation.
 * §Placement-type: the RESOLVED registry type is the authority for a ref placement — templates carry an
 * authored t:"text" on document refs (builder default), which rendered the seller documents as bare text
 * inputs (live bite, 2026-08-05); the schema `t` is only the fallback. §Multi-file: placement ?? registry
 * `multiple`. For a mirror, presentation comes from the SOURCE field — authored mirrors carry the
 * builder-default t:"text" and no options, which would render نوع العمل as a bare text input.
 */
const presentField = (
  field: FormField,
  global: StepGlobal | undefined,
  upstreamField: ReturnType<typeof upstreamFieldOf>
): FormField => {
  if (global) {
    const definition = global.definition as { paymentProof?: boolean } | undefined;
    return {
      ...field,
      l: field.l ?? global.label ?? field.ref ?? "",
      t: global.type ?? field.t ?? "text",
      multiple: field.multiple ?? global.document?.multiple,
      paymentProof:
        field.paymentProof ?? global.document?.paymentProof ?? definition?.paymentProof,
      showHistory: field.showHistory ?? global.showHistory,
    };
  }
  if (upstreamField) {
    return {
      ...field,
      // firstFilled, deliberately not ??: an authored EMPTY label must fall through to the source's.
      l: firstFilled(field.l, upstreamField.label, field.ref),
      t: upstreamField.type ?? field.t ?? "text",
      opt: field.opt ?? upstreamField.options ?? undefined,
    };
  }
  return field;
};

/** §Live-gates: may a rule the client fully understands overrule the server's `active: false`? */
const revivedByLiveGates = (
  field: FormField,
  liveGates: unknown,
  liveGateFacts: Record<string, unknown>
): boolean =>
  liveGates !== null &&
  preconditionSupported(field.precondition) &&
  evaluatePreconditionState(field.precondition, liveGateFacts) === "visible";

/** §Modals: the payment-plan modal edits the plan's own fields; every other widget owns nothing. */
const ownedPlanFields = (
  global: StepGlobal,
  planOwned: { fields: { key: string; field: FormField }[] } | null
): { key: string; field: FormField }[] | undefined =>
  global.widget?.widget === "paymentPlanCalculator" ? planOwned?.fields : undefined;

/** §34 (QA): a document upload must name the REGISTRY key (the placement's ref) — fieldKeyOf prefers an
 * authored `k`, and a k-keyed upload misses the document's OCR/extraction config in workflow-manager, so
 * the file attached but the extracted fields sat "pending" forever. WM uploads always went by ref. */
const documentKeyOf = (presented: FormField, key: string): string => presented.ref ?? key;

/** The placement's resolved upload rows, reshaped to the flat list DocumentField renders. */
const placementUploadsOf = (global: StepGlobal | undefined, key: string): StepUpload[] | null =>
  global?.document?.uploads?.length
    ? global.document.uploads.map((upload) => ({
        uid: upload.uid,
        fileName: upload.fileName,
        fieldKey: key,
        decision: upload.decision as FileDecision,
        rejectionReason: upload.rejectionReason ?? null,
        decisionAttachments: upload.decisionAttachments,
        payment: upload.payment,
      }))
    : null;

/** Placement rows when resolved, else the step's flat uploads filtered to this field's keys. */
const uploadsFor = (
  placementUploads: StepUpload[] | null,
  detail: StepDetail,
  key: string,
  ref: string | undefined
): StepUpload[] =>
  placementUploads ??
  (detail.uploads ?? []).filter(
    (upload) => upload.fieldKey === key || (ref !== undefined && upload.fieldKey === ref)
  );

/** §paymentProof: WM maps this placement's payment questions to fact keys and its upload route refuses a
 * file without the answers. Read them off the LIVE form (not the saved snapshot) so attaching right after
 * choosing a method works, which is exactly how an agent fills this step. */
const paymentAnswersOf = (
  presented: FormField,
  values: Record<string, unknown>
): { method?: string; mobileNumber?: string; transactionReferenceId?: string } | undefined => {
  if (!presented.paymentProof || presented.paymentFields === undefined) return undefined;
  const map = presented.paymentFields;
  const read = (factKey?: string): string | undefined => {
    if (!factKey) return undefined;
    const raw = values[factKey];
    return raw === undefined || raw === null || raw === "" ? undefined : String(raw);
  };
  return {
    method: read(map.method),
    mobileNumber: read(map.mobileNumber),
    transactionReferenceId: read(map.transactionReferenceId),
  };
};

/** Historical rows use every known proof signal. A placement false still controls new-upload
 * collection, but must not erase the resolved registry identity of an existing proof document. */
const proofDisplayContextOf = (
  presented: FormField,
  global: StepGlobal | undefined
): boolean =>
  presented.paymentProof === true ||
  global?.document?.paymentProof === true ||
  (global?.definition as { paymentProof?: boolean } | undefined)?.paymentProof === true;

/** A document card is read-only when the step is settled, the caller cannot edit, the placement is a
 * view twin, or a precondition locks the key. */
const documentDisabled = (
  presented: FormField,
  key: string,
  settled: boolean,
  agentEditable: boolean,
  lockedFields: Set<string>
): boolean => settled || !agentEditable || presented.mode === "view" || lockedFields.has(key);

/** §Recap (mirror): an un-opted mirror displays the SOURCE step's answer; the draft only shadows it. */
const mirrorRecapValue = (
  detail: StepDetail,
  field: FormField,
  values: Record<string, unknown>,
  key: string
): unknown => detail.upstreamValues?.[field.ref ?? ""] ?? values[key];

/** §Editable-mirrors: the local draft wins once the agent types; an untouched mirror shows the
 * server-resolved source value, a placement its canonical value (WM's resolveFieldDisplay). */
const resolvedFieldValue = (
  values: Record<string, unknown>,
  key: string,
  field: FormField,
  isUpstreamMirror: boolean,
  detail: StepDetail,
  global: StepGlobal | undefined
): unknown =>
  values[key] ?? (isUpstreamMirror ? detail.upstreamValues?.[field.ref ?? ""] : global?.value);

/** §Field-review: only a REJECTED verdict is a send-back the agent must act on — an APPROVED one carries
 * no instruction and would read as an alarm on a field that is already fine. */
const returnedReasonOf = (detail: StepDetail, key: string): string | null | undefined =>
  detail.fieldReviews?.[key]?.status === "REJECTED" ? detail.fieldReviews[key]?.comment : undefined;

/** The sibling value a dependent option source (compounds `dependsOn` developers) resolves against. */
const dependentParentValue = (
  presented: FormField,
  registrySources: OptionSource[] | undefined,
  valueBySourceKey: Record<string, unknown>
): unknown =>
  presented.optionsFrom
    ? valueBySourceKey[
        registrySources?.find((source) => source.key === presented.optionsFrom)?.dependsOn ?? ""
      ]
    : undefined;

/** §Review-appeal: a non-document item anchors under the field itself, below the reviewer's
 * "Returned: …" reason — a concrete field by its flat key, a pool value by its placement ref.
 * (A document anchors on the FILE ROW inside DocumentField instead — several files can share one
 * placement key, and each carries its own decision, its own reason and its own decider.) */
const appealAnchorOf = (global: StepGlobal | undefined, key: string): AppealAnchor =>
  global ? { kind: "value", fieldKey: global.ref } : { kind: "field", fieldKey: key };

/** §AgentAccess: why the form is visible but not editable (reason → agent-facing caption). */
const AGENT_LOCK_MESSAGE: Record<string, string> = {
  NOT_OPENED:
    "This step hasn't opened yet — it unlocks once its prerequisites are completed.",
  READ_ONLY: "You have view-only access to this step.",
  LOCKED: "This step is locked by a precondition.",
  SETTLED: "This step has already been decided.",
  // §Case-hold: without this the fallback reads "view-only access", which blames the agent's permissions.
  CASE_ON_HOLD:
    "This application is on hold — nothing can be changed until a Workflow Manager admin resumes it.",
};

const pendingCompletionMessage = (pending: boolean, blockedBy: string[] | undefined): string => {
  if (!pending) return "";
  if (blockedBy?.length) return `Completes when unblocked: ${blockedBy.join(", ")}`;
  return "Completes when unblocked";
};

const DraftSaveControls = ({
  mobileValue,
  savedMobileValue,
  dirty,
  saving,
  busy,
  onSave,
}: {
  mobileValue: unknown;
  savedMobileValue: unknown;
  dirty: boolean;
  saving: boolean;
  busy: boolean;
  onSave: () => Promise<unknown>;
}) => {
  const theme = useTheme();
  const colors = theme.nawy.colors;
  // §Phone-guard: normalization keeps a legacy "01…" rewritten as "+201…" from counting as a change.
  const phoneChanged = normalizePhone(String(mobileValue ?? "")) !==
    normalizePhone(String(savedMobileValue ?? ""));
  const idleMessage = phoneChanged
    ? "Unsaved phone change — press Save to verify and store it"
    : "Autosaving…";
  const message = saving ? "Saving…" : idleMessage;

  return (
    <>
      {/* §NoSaveButton: the mobile is the one field autosave leaves alone — its explicit save
          runs CRM phone verification. Other edits show autosave progress without a Save button. */}
      {phoneChanged && (
        <Button
          variant="outlined"
          disabled={busy}
          onClick={() => { onSave().catch(() => undefined); }}
          sx={secondaryButtonSx(theme)}
        >
          Save
        </Button>
      )}
      {(dirty || saving) && (
        <Typography
          variant="caption"
          sx={{
            alignSelf: "center",
            color: saving ? colors.semantic.text.secondary : colors.semantic.text.extension.warning,
            fontWeight: 600,
          }}
        >
          {message}
        </Typography>
      )}
    </>
  );
};

/**
 * Dynamic step form: renders every section/field of the schema, seeded with the latest
 * submission over the prefill. Save persists a draft; action buttons ("Request iscore",
 * "Submit documents", …) save then complete the step. Blocked steps stay editable
 * (prefill is allowed) but cannot be completed until their prerequisites clear.
 */
const StepForm = ({
  step,
  caseUid,
  detail,
  schema,
  mutations,
}: {
  step: PersonaStep;
  caseUid: string | null;
  detail: StepDetail;
  schema: FormSection[];
  mutations: ReturnType<typeof useStepMutations>;
}) => {
  const theme = useTheme();
  const colors = theme.nawy.colors;
  // §OptionSources: shared tenant registry (cached) — read here only to learn each source's
  // `dependsOn`, so the right sibling value is handed to a dependent select.
  const registrySources = useOptionSources().data?.sources;

  const initialValues = useMemo(() => {
    const served: Record<string, unknown> = {
      ...(detail.prefill ?? {}),
      ...((detail.submissions?.[0]?.data as Record<string, unknown>) ?? {}),
    };
    // §Derive-on-seed: evaluate the derive plan the moment the form is seeded, exactly as
    // workflow-manager's renderer does in its mount effect (form-renderer.tsx: `if (storedNumber
    // !== computed) onChange(key, computed)`). Without this a calculated field that was never
    // saved sits ABSENT, and an absent value is exempt from validation everywhere — so the Loan
    // Amount's authored caps could only fire once something had been saved, or on a case someone
    // had already saved in workflow. Nothing is `touched` at seed time: a saved override on a
    // non-editable derived field is not a manual value, it is stale.
    return {
      ...served,
      ...evaluateDerivedFills(
        (detail.formSchema as FormSection[] | undefined) ?? [],
        served,
        new Set<string>()
      ),
    };
  }, [detail.prefill, detail.submissions, detail.formSchema]);
  const [values, setValues] = useState<Record<string, unknown>>(initialValues);
  // §Autosave: what the workflow is KNOWN to hold — advanced by every successful save (with the
  // exact payload sent) and by a server refresh. Drives the dirty flag and the refresh merge below.
  const [savedSnapshot, setSavedSnapshot] = useState<Record<string, unknown>>(initialValues);
  const savedSnapshotRef = useRef(savedSnapshot);
  savedSnapshotRef.current = savedSnapshot;
  const valuesRef = useRef(values);
  valuesRef.current = values;
  useEffect(() => {
    // §Autosave-merge: a refetch (e.g. a caseChanged autosave, or another surface invalidating the
    // tree) must never clobber keystrokes typed after the save was snapshotted — local values that
    // differ from the last-known-saved state survive the refresh; everything else adopts the server's.
    setValues((previous) => {
      const merged: Record<string, unknown> = { ...initialValues };
      for (const [key, value] of Object.entries(previous)) {
        if (value !== savedSnapshotRef.current[key]) {
          merged[key] = value;
        }
      }
      return merged;
    });
    setSavedSnapshot(initialValues);
  }, [initialValues]);
  // §44 (QA): an edit that has not reached the workflow must never read as saved — the buffer can
  // outlive a tab switch (the component stays mounted), so the CRM showed uncommitted values with
  // no distinction while the Workflow Manager still held the old ones. Compared against the
  // last-SAVED snapshot (not the last-fetched one), so autosaved fields read as saved.
  const dirty = useMemo(
    () => Object.keys(values).some((key) => values[key] !== savedSnapshot[key]),
    [values, savedSnapshot]
  );

  // §Live-gates: field/section/option visibility is judged CLIENT-SIDE per keystroke — the raw
  // rules ride the fetched schema, the server ships every cross-step operand (`preconditionFacts`),
  // and the live draft overlays both. Choosing "Individual" reveals its dependent fields in the
  // same render instead of after a save round-trip. Rules the mirror can't judge (review/date ops)
  // fall back to the server-evaluated sets per element; an older WM deploy (no preconditionFacts)
  // disables the mirror entirely — the server sets then behave exactly as before.
  const serverGates = useMemo(
    () => ({
      hiddenFieldKeys: detail.hiddenFieldKeys ?? [],
      lockedFieldKeys: detail.lockedFieldKeys ?? [],
      hiddenSectionNames: detail.hiddenSectionNames ?? [],
      hiddenOptionValues: detail.hiddenOptionValues ?? {},
    }),
    [
      detail.hiddenFieldKeys,
      detail.lockedFieldKeys,
      detail.hiddenSectionNames,
      detail.hiddenOptionValues,
    ]
  );
  const liveGateFacts = useMemo(() => {
    const facts: Record<string, unknown> = { ...(detail.preconditionFacts ?? {}) };
    // §Qualified-self: templates reference a step's OWN answers through the upstream syntax too —
    // Unit Data's document rules read "@s18:Seller.Individual Category", not the flat key — so the
    // draft must shadow BOTH spellings, or those gates keep evaluating the stale saved fact and
    // the reveal waits for the autosave refetch (live bite, 2026-08-05).
    const stepOrder = (detail as { stepOrder?: number }).stepOrder;
    for (const [key, value] of Object.entries(values)) {
      if (value === undefined) continue; // never shadow a server fact with an unset buffer key
      facts[key] = value;
      if (stepOrder !== undefined) {
        facts[`@s${stepOrder}:${key}`] = value;
      }
    }
    return facts;
  }, [detail, values]);
  const liveGates = useMemo(() => {
    if (detail.preconditionFacts === undefined) return null;
    return computeLiveGates(schema, liveGateFacts, serverGates);
  }, [detail.preconditionFacts, schema, liveGateFacts, serverGates]);
  const hiddenFields = useMemo(
    () => liveGates?.hiddenFields ?? new Set(serverGates.hiddenFieldKeys),
    [liveGates, serverGates]
  );
  // save closures run on a debounce chain — they need the CURRENT gate verdict, not a capture
  const hiddenFieldsRef = useRef(hiddenFields);
  hiddenFieldsRef.current = hiddenFields;
  const lockedFields = useMemo(
    () => liveGates?.lockedFields ?? new Set(serverGates.lockedFieldKeys),
    [liveGates, serverGates]
  );
  const hiddenSections = useMemo(
    () => liveGates?.hiddenSections ?? new Set(serverGates.hiddenSectionNames),
    [liveGates, serverGates]
  );
  const hiddenOptionsFor = (key: string): string[] => {
    if (liveGates) return [...(liveGates.hiddenOptions.get(key) ?? [])];
    return serverGates.hiddenOptionValues[key] ?? [];
  };
  const hiddenActions = useMemo(
    () => new Set(detail.hiddenActionKeys ?? []),
    [detail.hiddenActionKeys]
  );

  /** resolved placement presentation by ref (label/type/active/uploads) — see §Placements below */
  const globalByRef = useMemo(
    () => new Map<string, StepGlobal>((detail.globals ?? []).map((g) => [g.ref, g])),
    [detail.globals]
  );

  const settled = step.status === "APPROVED" || step.status === "SKIPPED";
  // §AgentAccess: the agent's own permission on this step, folded with its state server-side
  // (write+opened ⇒ editable; read, not-yet-opened, settled or precondition-locked ⇒ not).
  // Detail is fresher than the board's copy; absent on older WM deploys ⇒ legacy behavior.
  const agentAccess = detail.agentAccess ?? step.agentAccess;
  // §AgentView: the authored AGENT mode caps this channel — 'view' is read-only even when the
  // capability layer would allow writes (an empty WRITER bucket is open to all internal users).
  const agentEditable =
    !detail.paymentPlanUpdateBlockReason && step.clientAccess?.AGENT !== "view" && (agentAccess ? agentAccess.editable : true);
  // §ApproveLevel: action buttons (complete / reject / effect actions) require the APPROVER
  // capability server-side — a WRITER without approve gets Save only, instead of buttons that
  // always come back "Only an approver can take this action". Absent agentAccess (older WM
  // deploys) keeps the legacy behavior.
  const agentMayApprove = !detail.paymentPlanUpdateBlockReason && (agentAccess ? agentAccess.canApprove : true);
  // §Opened-consistency: `blocked` gates completion only for a step that hasn't opened yet
  // (NOT_STARTED). An opened step (IN_PROGRESS / REWORK_REQUIRED) is completable — a stale
  // cross-block dependency must not disable Submit, matching the board's status-driven "opened".
  const pending = step.status === "NOT_STARTED" && step.blocked;
  /**
   * §Autosave-UX (2026-08-08): a BACKGROUND save must not take the screen hostage.
   *
   * `busy` disables Save, Submit and every action button. It was true for any in-flight save —
   * including the per-field autosave that fires a second after the agent stops typing. With a save
   * round-trip in the hundreds of ms, that meant the buttons spent most of a form-filling session
   * disabled, flickering back on between keystrokes.
   *
   * Nothing was bought by that: every terminal path already serializes behind `autosaveChain` —
   * `saveDraft` chains explicitly, and `runAction` awaits `saveDraft` before completing — so a click
   * landing mid-autosave waits for it rather than racing it. Only an EXPLICIT save (the Save button,
   * or the save `runAction` performs) still reads as busy.
   */
  const busy =
    (mutations.save.isLoading && !mutations.save.variables?.quiet) ||
    mutations.complete.isLoading;

  // §DBR-parity (prototype): a field with a `validate {op:'gte', valueField, factor}` rule (the
  // Down Payment ≥ 20% of Cash Price) AUTO-FILLS to factor × source while the agent hasn't touched
  // it — typing the cash price suggests the 20% DP, editing the DP stops the suggestion. Config-
  // driven from the same rule the server enforces; no hardcoded field names.
  const touched = useMemo(() => new Set<string>(), []);

  /**
   * §Derive-override (WM parity): typing over a CALCULATED field only sticks when the template says
   * the field may be overridden — `deriveEditable`. workflow-manager's form renderer claims the
   * override on exactly that condition (form-renderer.tsx `field.derive && field.deriveEditable`);
   * for anything else the derive effect simply refills the computed value over what was typed.
   *
   * The CRM was marking EVERY edited key as touched, and `evaluateDerivedFills` skips touched keys —
   * so a typed value on a calculated field stuck here while the same keystroke bounced back in WM.
   * "Payment Plan.Loan Amount" is the visible case: `src: "calc"`, derive
   * `[Cash Price] - [Down Payment (20%)]`, no `deriveEditable`.
   */
  const fieldByKey = useMemo(() => {
    const map = new Map<string, FormField>();
    for (const section of schema) {
      for (const field of section.fields ?? []) map.set(fieldKeyOf(section, field), field);
    }
    return map;
  }, [schema]);
  const claimsDeriveOverride = (key: string): boolean => {
    const field = fieldByKey.get(key);
    return !(field?.derive && !field.deriveEditable);
  };
  // §Derive: the keys the engine is actually calculating (WM's `derivedKeys`) — what renders
  // read-only, here and in the plan modal. NOT "has a derive": one it cannot compile stays typeable.
  const derivedKeys = useMemo(() => calculatedKeys(schema), [schema]);
  // §Rule-arrays: shared with the plan modal (utils/validate `ruleFactorFills`), so an authored rule
  // LIST — the shape the current template uses — suggests on both surfaces, not just a lone rule.
  const derivedFillsOf = (sourceKey: string, sourceValue: unknown) =>
    ruleFactorFills(fieldEntriesOf(schema), sourceKey, sourceValue, touched);
  /**
   * §Autosave: every field entry reaches the workflow on its own, debounced PER FIELD — each key
   * holds its own timer, so continuous typing in one field defers only that field's flush while a
   * field left half a second ago still saves on schedule. The flush itself is serialized through a
   * promise chain (never two saves racing) and snapshots the WHOLE buffer, so whichever timer fires
   * carries every settled edit with it.
   *
   * §Phone-guard: the client-mobile field is the one deliberate exception — its saves must clear
   * the crm-backend telephone reconciliation first (409 = another customer's number), which is not
   * a background-safe call to spam with half-typed digits. An unsaved mobile edit therefore rides
   * along as its LAST SAVED value and only persists through the explicit Save / Submit path.
   */
  const autosaveTimers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const autosaveChain = useRef<Promise<unknown>>(Promise.resolve());
  useEffect(() => {
    const timers = autosaveTimers.current;
    return () => timers.forEach((timer) => clearTimeout(timer));
  }, []);

  // §OnlySubmitChosen (product ask, 2026-08-06): never send a hidden branch's stale answers —
  // the engine drops hidden keys under ITS gate view, but the client's view is fresher mid-edit
  // (the flip that hid them may be IN this very payload). Both save paths go through this.
  const visibleValues = (): Record<string, unknown> =>
    Object.fromEntries(
      Object.entries(valuesRef.current).filter(
        ([key]) => !hiddenFieldsRef.current.has(key)
      )
    );

  const flushAutosave = () => {
    autosaveChain.current = autosaveChain.current
      .then(async () => {
        const snapshot = savedSnapshotRef.current;
        const data = { ...visibleValues() };
        if (data[CLIENT_MOBILE_KEY] !== snapshot[CLIENT_MOBILE_KEY]) {
          data[CLIENT_MOBILE_KEY] = snapshot[CLIENT_MOBILE_KEY];
        }
        const changed = Object.keys(data).some(
          (key) => data[key] !== snapshot[key]
        );
        if (!changed) return;
        await mutations.save.mutateAsync({ data, quiet: true });
        setSavedSnapshot(data);
      })
      .catch(() => undefined); // failures already toast via the mutation; the dirty flag stays on
  };

  const scheduleAutosave = (key: string) => {
    if (settled || !agentEditable) return;
    const timers = autosaveTimers.current;
    const existing = timers.get(key);
    if (existing) clearTimeout(existing);
    timers.set(
      key,
      setTimeout(() => {
        timers.delete(key);
        flushAutosave();
      }, AUTOSAVE_DEBOUNCE_MS)
    );
  };

  const setValue = (key: string, value: unknown) => {
    if (claimsDeriveOverride(key)) touched.add(key);
    setValues((previous) => {
      const next = { ...previous, [key]: value, ...derivedFillsOf(key, value) };
      // §Derive: re-evaluate every `field.derive` expression (e.g. Loan Amount = Cash − DP) over the
      // whole schema, skipping fields the agent has typed over — same engine the WM Tasks UI uses.
      return { ...next, ...evaluateDerivedFills(schema, next, touched) };
    });
    scheduleAutosave(key);
  };

  // §ManualPlan (product ask, 2026-08-06): the plan modal's Save commits its LOCAL draft in one
  // beat — state for the UI, and the ref the save chain reads, so the saveDraft() that follows in
  // the same click persists exactly these values (React batching would otherwise feed it the old
  // ones). Derived fills (Loan Amount = Cash − DP) recompute over the committed set.
  const applyFacts = (entries: Record<string, unknown>) => {
    if (!Object.keys(entries).length) return;
    Object.keys(entries).forEach((key) => {
      if (claimsDeriveOverride(key)) touched.add(key);
    });
    const merged = { ...valuesRef.current, ...entries };
    const withDerived = { ...merged, ...evaluateDerivedFills(schema, merged, touched) };
    valuesRef.current = withDerived;
    setValues(withDerived);
  };

  const saveDraft = () => {
    // Chained behind any in-flight autosave: two concurrent POSTs are last-writer-wins on the
    // server, so an autosave landing late could overwrite this save's phone edit with the guarded
    // (older) value. The chain also makes later autosaves wait for this save's snapshot advance.
    const run = autosaveChain.current.then(() => {
      const data = visibleValues();
      const mobile = data[CLIENT_MOBILE_KEY];
      return mutations.save
        .mutateAsync({
          data,
          telephone:
            typeof mobile === "string" && mobile.trim() ? mobile : undefined,
        })
        .then((result) => {
          setSavedSnapshot(data);
          return result;
        });
    });
    autosaveChain.current = run.catch(() => undefined);
    return run;
  };

  // §W3.2 Credit-feedback cards: detect the "Resolution path" radio (options ⊆ the known credit
  // paths) + its confirm-path action, so the plain radio + Submit becomes the Figma card stack.
  const creditFeedback = useMemo(() => {
    const action =
      (detail.actions ?? []).find((a) => a.key === "confirm-path") ??
      (detail.actions ?? []).find((a) => a.outcome === "APPROVED" && a.key);
    if (!action?.key) return null;
    for (const section of schema) {
      for (const field of section.fields) {
        const opts = (field.opt as string[] | undefined) ?? [];
        if (field.t === "radio" && isCreditFeedbackOptions(opts)) {
          return {
            key: fieldKeyOf(section, field),
            options: opts,
            actionKey: action.key,
          };
        }
      }
    }
    return null;
  }, [schema, detail.actions]);

  // §OptionSources: value currently held by the field bound to each source key, so a dependent
  // source (compounds `dependsOn` developers) resolves against the live sibling selection rather
  // than a stale submission. Rebuilt as the agent types — picking a developer refills Compound.
  const valueBySourceKey = useMemo(() => {
    const map: Record<string, unknown> = {};
    for (const section of schema) {
      for (const field of section.fields) {
        if (field.optionsFrom) {
          map[field.optionsFrom] = values[fieldKeyOf(section, field)];
        }
      }
    }
    return map;
  }, [schema, values]);

  // §Rules: the template's validate rules, evaluated live — the engine's own config drives the
  // red outline + message (decision 2026-07-30), so new rules light up with no FE change.
  // §Rule-arrays: liveGateFacts carries the case facts + upstream `@s…` keys the branch
  // conditions name (e.g. "@s6:Finance Product.Finance Product") on top of the live values.
  const ruleErrors = useMemo(
    () => fieldRuleErrors(schema, liveGateFacts),
    [schema, liveGateFacts]
  );

  const approvedAmount = useMemo(() => {
    const raw = globalByRef.get(DBR_MAX_REF)?.value ?? values[DBR_MAX_REF];
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : undefined;
  }, [globalByRef, values]);

  /**
   * §Modals: nothing terminal fires straight off a click any more. `confirm` holds the request the
   * modal renders plus the work to run once the agent accepts — so one shell serves the
   * credit-feedback paths AND every comment-gated action (which used to open a `window.prompt`).
   */
  const [confirm, setConfirm] = useState<{
    request: ActionConfirmRequest;
    attachmentContext?: NoteAttachmentContext;
    run: (comment?: string, attachmentUids?: string[]) => Promise<unknown>;
  } | null>(null);
  const [dbrOpen, setDbrOpen] = useState(false);

  /**
   * §Modals: the payment-plan widget owns its INPUT FIELDS. The design's plan card (12107:56541)
   * shows only a summary + "Edit plan"; the inputs live in the modal (12107:56750). The owned
   * fields are hidden from the card FIELD BY FIELD (on the live template the widget placement and
   * its inputs share one "Payment Plan" section — hiding the whole section would swallow the
   * widget, the plan file and the finance preview with it). Commercial/Residential and the
   * derived Loan Amount moved INTO the modal too (product ask, 2026-08-05) — the card shows
   * only the plan summary + Edit plan.
   */
  const planOwned = useMemo(() => {
    const global = (detail.globals ?? []).find(
      (candidate) => candidate.widget?.widget === "paymentPlanCalculator"
    );
    if (!global) return null;
    const owned = new Set(paymentPlanInputKeys(global.widget?.parameters));
    const fields = schema.flatMap((section) =>
      section.fields
        .map((field) => ({ key: fieldKeyOf(section, field), field }))
        .filter(
          ({ key, field }) =>
            owned.has(key) &&
            !hiddenFields.has(key) &&
            field.mode !== "view" &&
            !isDocumentField(field)
        )
    );
    if (!fields.length) return null;
    return { keys: new Set(fields.map(({ key }) => key)), fields };
  }, [detail.globals, schema, hiddenFields]);

  /** §Modals: the design's "Preview DBR" reads the step's own dbrCalculator placement. */
  const dbrEligibility = useMemo(
    () =>
      (detail.globals ?? []).find((global) => global.widget?.widget === "dbrCalculator")
        ?.widget?.dbrEligibility,
    [detail.globals]
  );

  /** Card CTA → this path's confirmation modal; accepting persists the choice and fires confirm-path. */
  const askCreditPath = (optionValue: string) => {
    const copy = creditPathConfirm(optionValue, approvedAmount);
    if (!copy) return;
    setConfirm({
      request: {
        title: copy.title,
        message: copy.message,
        alert: copy.alert,
        footnote: copy.footnote,
        amount: copy.amountPanel ? approvedAmount : undefined,
        confirmLabel: copy.confirmLabel,
      },
      run: async () => {
        const next = { ...values, [creditFeedback!.key]: optionValue };
        setValues(next);
        try {
          // Behind the autosave chain like every other terminal path — a debounced autosave still in
          // flight is last-writer-wins on the server and would otherwise land AFTER this resolution
          // save, reverting the very answer being confirmed. (The other paths get this via saveDraft.)
          await autosaveChain.current;
          await mutations.save.mutateAsync({ data: next });
        } catch {
          throw new Error("Could not save the selected credit path.");
        }
        return mutations.complete.mutateAsync({ actionKey: creditFeedback!.actionKey });
      },
    });
  };

  const runAction = async (
    action: { key: string | null; outcome?: string; effects?: unknown[] },
    comment?: string,
    attachmentUids?: string[]
  ): Promise<unknown> => {
    // persist the current draft first so completion gates evaluate what's on screen;
    // a failed save (e.g. phone conflict) already toasts — don't attempt completion.
    // §ChangeUnit: NOT on a decided step — post-approval actions live on APPROVED, where any
    // save is refused with "This step is already decided" before the action could fire.
    const decided =
      step.status === "APPROVED" ||
      step.status === "REJECTED" ||
      step.status === "SKIPPED";
    if (!decided) {
      await saveDraft();
    }
    if (actionUsesInvokeEndpoint(action)) {
      return mutations.invokeAction.mutateAsync({ actionKey: action.key, comment, attachmentUids });
    }
    return mutations.complete.mutateAsync({
      ...(action.key ? { actionKey: action.key } : { outcome: "APPROVED" }),
      ...(comment ? { comment } : {}),
      ...(attachmentUids?.length ? {attachmentUids} : {}),
    });
  };

  const handleAction = async (action: {
    key: string | null;
    label?: string;
    outcome?: string;
    requiresComment?: boolean;
    effects?: unknown[];
    confirmMessage?: string;
  }) => {
    // §Comment-gate: the engine 400s a completion whose action (or a REJECTED outcome) needs a comment.
    // Collect it up-front — matches the WM Tasks UI (resolveRequiredComment) — but in the design's
    // modal (frame 12107:52472 "Escalate"), not a browser prompt. An authored `confirmMessage`
    // (Change Unit's blast-radius warning) opens the same modal even without a comment.
    const needsComment = actionNeedsComment({
      actionRequiresComment: action.requiresComment,
      isRejectOutcome: action.outcome === "REJECTED",
      mandatoryComments: detail.mandatoryComments,
      actionEffects: action.effects,
      stepEffectsOnComplete: detail.effectsOnComplete,
      // a non-terminal button carries no outcome — it only fires its effects
      terminal: Boolean(action.outcome),
    });
    if (needsComment || action.confirmMessage) {
      // action labels carry the flow's direction glyphs ("Escalate to MD ↩"); the design's modal
      // spells the title and verb out. Only the escalate frame (12107:52472) exists, so only the
      // escalate actions get design copy — everything else falls back to the cleaned-up label.
      const fallback = (action.label ?? "Confirm").replace(/[↩→←↺]/g, "").trim();
      const escalating = action.key?.startsWith("escalate");
      setConfirm({
        request: {
          title: escalating ? "Escalate appraisal report" : fallback,
          message: action.confirmMessage,
          commentRequired: Boolean(needsComment),
          confirmLabel: escalating ? "Submit escalation" : fallback,
          destructive: action.outcome === "REJECTED",
          // a comment-collecting modal shows only its verb (design frame 12107:52472); an authored
          // confirmMessage is a real decision — keep Cancel visible for it
          hideCancel: !action.confirmMessage,
        },
        attachmentContext: needsComment
          ? {
              kind: "step-action",
              stepUid: step.uid,
              ...(action.key ? {actionKey: action.key} : {}),
            }
          : undefined,
        run: (comment, attachmentUids) => runAction(action, comment, attachmentUids),
      });
      return;
    }
    await runAction(action);
  };

  const actions = (detail.actions ?? []).filter(
    (action) =>
      action.label &&
      !(action.key && hiddenActions.has(action.key)) &&
      // §ChangeUnit: `available` is the server's status fit — a post-approval action must not
      // render while the step is IN_PROGRESS (and vice versa). Absent on older WM deploys ⇒ keep.
      action.available !== false
  );

  // §Recap (design 12107): a DONE or view-only step reads as label + plain value rows — the design's
  // settled state — not a wall of dimmed textboxes. Documents keep their cards (Download is the task).
  const formReadOnly = settled || !agentEditable;
  const recapValue = (field: FormField, raw: unknown): string => {
    if (raw === undefined || raw === null || raw === "") return "—";
    if (Array.isArray(raw)) return raw.map(String).join(", ");
    if (typeof raw === "boolean") return raw ? "Yes" : "No";
    if (
      (field.t === "money" || field.t === "number") &&
      Number.isFinite(Number(raw))
    ) {
      const grouped = formatThousands(String(raw));
      return field.t === "money" ? `EGP ${grouped}` : grouped;
    }
    return String(raw);
  };
  const recapRow = (key: string, field: FormField, raw: unknown) => (
    <Box key={key} dir="auto">
      {/* design label token: Poppins 11/16 #636363 — same as the editable form's labels */}
      <Typography
        sx={{ fontSize: 11, lineHeight: "16px", color: colors.semantic.text.secondary }}
      >
        {field.l}
      </Typography>
      <Typography
        sx={{
          // design value token: Body/md - Regular, Poppins 14/20 #313131 (node 12107:47499) —
          // the recap value sits one step above its 11/16 label, not the 16/24 it used to be
          fontSize: 14,
          lineHeight: "20px",
          // §Bold-amounts (product ask, 2026-08-06): money/number recap values read as figures
          fontWeight: field.t === "money" || field.t === "number" ? 700 : 400,
          color: colors.semantic.text.primary,
          overflowWrap: "anywhere",
        }}
      >
        {recapValue(field, raw)}
      </Typography>
      {detail.fieldReviews?.[key]?.status === "REJECTED" && detail.fieldReviews[key]?.comment && (
        <Typography sx={{fontSize: 11, color: colors.semantic.text.extension.negative}}>
          Returned: {detail.fieldReviews[key]?.comment}
        </Typography>
      )}
      <NoteAttachmentList attachments={detail.fieldReviews?.[key]?.attachments} />
    </Box>
  );

  // §Decision-gate (WM parity): the server refuses to APPROVE while anything is owed — disable the
  // approve-side buttons and SAY WHY on hover, instead of letting the click come back a 400. The
  // live draft rides along so a just-typed value on a review-required placement gates immediately.
  // Reject-side buttons stay live either way, exactly like the WM action bar.
  // §Live-gates (product ask, 2026-08-06): the Submit gate re-judges on every keystroke with the
  // LOCAL visibility verdict — a branch flip that reveals new required work (or hides the old
  // branch's) moves the button on the same beat, not after the autosave refetch.
  const approveBlockers = useMemo(
    () =>
      computeStepBlockers(
        detail,
        values,
        liveGates ? { hidden: hiddenFields, locked: lockedFields } : undefined
      ),
    [detail, values, liveGates, hiddenFields, lockedFields]
  );
  const approveBlocked = approveBlockers.length > 0;
  const blockedReason = approveBlocked ? blockersTooltip(approveBlockers) : "";
  const pendingReason = pendingCompletionMessage(pending, step.blockedBy);
  /**
   * Reason a given action button is disabled right now — becomes its hover tooltip.
   *
   * §MD-bypass (Codex-confirmed fix): a keyed action with no `outcome` (`actionUsesInvokeEndpoint`)
   * never completes the step — it always fires through invoke-action, pressing the button IS the
   * effect. The completion-blockers gate (`approveBlocked`) exists to stop a click that would 400
   * on an incomplete form, and that can never happen here, so it must not gate this button either.
   * This is exactly the case that needed fixing: "Request MD bypass" on the Medical Questionnaire
   * step was disabled by this same tooltip while required answers were still empty — precisely
   * when a bypass is needed. A legacy no-outcome, NO-KEY action (completes via the APPROVED path)
   * is untouched: `actionUsesInvokeEndpoint` is false for it, so it keeps the gate.
   */
  const disabledReasonFor = (action: { key: string; outcome?: string; requiresMet?: boolean }): string => {
    if (action.requiresMet === false) return "This action's requirements are not met yet.";
    if (pending) return pendingReason;
    if (
      action.outcome !== "REJECTED" &&
      approveBlocked &&
      !actionUsesInvokeEndpoint(action)
    ) {
      return blockedReason;
    }
    return "";
  };
  /** MUI can't anchor a tooltip on a disabled button — the span wrapper takes the hover. */
  const withReason = (reason: string, button: JSX.Element): JSX.Element =>
    reason ? (
      <Tooltip key={button.key ?? undefined} title={reason}>
        <span>{button}</span>
      </Tooltip>
    ) : (
      button
    );

  // Assembled as a value rather than returned directly so the appeal provider can wrap it below without
  // re-indenting the whole form.
  const historyView = stepHistoryView(detail.uiFlags);
  const body = (
    <Box>
      {/* §Decisions: the reviewer's send-back reason, above the form the agent has to fix. */}
      <DecisionPanel
        comments={conversationOf(detail.comments)}
        attention={
          step.status === "REWORK_REQUIRED" || step.status === "REJECTED"
        }
      />
      <Stack spacing={2.5}>
        {schema
          .filter((section) => !hiddenSections.has(section.name))
          .map((section) => (
          <Box key={section.name}>
            {section.name && (
              <Typography
                sx={{
                  // design group-caption token: Body/xs - Regular 11/16 Text/Secondary #636363
                  // ("Primary documents" / "Secondary documents", nodes 12107:47508 / 12107:47504).
                  // A semibold dark 12/18 heading out-shouted the fields it introduces.
                  fontSize: 11,
                  lineHeight: "16px",
                  fontWeight: 400,
                  color: colors.semantic.text.secondary,
                  mb: 1,
                }}
              >
                {section.name}
              </Typography>
            )}
            <Stack spacing={2}>
              {section.fields.map((field) => {
                const key = fieldKeyOf(section, field);
                // condition unmet ⇒ the engine says this field does not apply to this case
                if (hiddenFields.has(key)) return null;
                // §Modals: the plan's typed inputs render inside the payment-plan modal instead
                if (planOwned?.keys.has(key)) return null;
                // §W3.2: the credit-feedback resolution radio renders as the Figma decision-card
                // stack (each card's CTA selects the path + confirms). Read-only viewers fall
                // through to the plain disabled radio below.
                if (
                  creditFeedback &&
                  key === creditFeedback.key &&
                  agentEditable &&
                  !settled
                ) {
                  const hidden = hiddenOptionsFor(key);
                  return (
                    <CreditFeedbackCards
                      key={key}
                      options={creditFeedback.options.filter(
                        (o) => !hidden.includes(o)
                      )}
                      approvedAmount={approvedAmount}
                      busy={busy}
                      onSelect={askCreditPath}
                      onViewDbr={() => setDbrOpen(true)}
                    />
                  );
                }
                // §Placements: a raw {ref, mode} schema field carries no label/type — its
                // presentation lives in the read model's resolved `globals` entry (presentField).
                // §Mirrors (QA follow-up): a qualified upstream reference (@s<order>:<fieldKey>)
                // holds no value of its own — it displays the SOURCE step's answer (e.g. the KYC
                // work-type on Credit Documents), matching the Workflow Manager form.
                // §Editable-mirrors (WM parity): UNLESS the placement opted into mode:'edit' — an
                // editable mirror submits its @s key and the engine routes the write back to the
                // source step, so re-picking نوع العمل re-derives the mandatory documents list.
                const isUpstreamMirror = Boolean(field.ref?.startsWith("@s"));
                const upstreamField = upstreamFieldOf(detail, field);
                const mirrorEditable = isMirrorEditable(upstreamField, field);
                const global =
                  field.ref && !isUpstreamMirror ? globalByRef.get(field.ref) : undefined;
                // §Live-gates: `active` is the SERVER's verdict on the placement's precondition —
                // re-judge it locally so a document/placement revealed by a choice the agent just
                // made (e.g. Seller Type → its documents) appears now, not after the autosave
                // refetch. Only a rule the mirror fully understands may overrule the server.
                if (
                  global &&
                  global.active === false &&
                  !revivedByLiveGates(field, liveGates, liveGateFacts)
                ) {
                  return null;
                }
                // §Widgets: a widget placement renders its full-width component (DBR calculator,
                // payment plan, send-to-credit preview) instead of a form input. The live form
                // values feed the payment-plan DBR-fit validation (prototype behavior).
                if (global?.widget) {
                  if (shouldHideLegacyHistoryWidget(historyView, global.widget.widget)) return null;
                  return (
                    <StepWidget
                      key={key}
                      widget={global.widget}
                      liveFacts={values}
                      // stepHistory reads the STEP's audit trail, not the case's
                      stepUid={step.uid}
                      disabled={settled || !agentEditable}
                      // compoundCheck WRITES (the routing fact + the submit gate's stamps) —
                      // refetch so the verdict, the gate and any revealed documents all land
                      onCaseChanged={mutations.refresh}
                      // the check reads SAVED facts, so the widget persists the draft first
                      saveDraft={saveDraft}
                      // §Modals: the payment-plan modal edits the plan's own fields
                      ownedFields={ownedPlanFields(global, planOwned)}
                      // §Derive: the keys THIS engine is calculating — the modal renders them
                      // read-only like WM's form renderer does (a derive it can't compute, or a
                      // conditional whose branch is unarmed, stays typeable).
                      calculatedKeys={derivedKeys}
                      onFactChange={setValue}
                      // §ManualPlan: the plan modal commits its local draft only on Save/Share
                      commitFacts={applyFacts}
                      // case facts incl. upstream `@s…` keys for `when` rule branches
                      ruleFacts={liveGateFacts}
                      fieldErrors={ruleErrors}
                    />
                  );
                }
                const presented = presentField(field, global, upstreamField);
                // §Review-appeal: a document anchors its appeal on the FILE ROW that was returned,
                // inside DocumentField, so its branch returns bare; everything else is wrapped with
                // the thread anchored under the item (see appealAnchorOf).
                if (isDocumentField(presented)) {
                  return (
                    <DocumentField
                      key={key}
                      field={presented}
                      fieldKey={documentKeyOf(presented, key)}
                      caseUid={caseUid}
                      stepUid={step.uid}
                      paymentAnswers={paymentAnswersOf(presented, values)}
                      proofDisplayContext={proofDisplayContextOf(presented, global)}
                      // §TypedDocs: the registry's allowed types for a multi-type placement
                      fileTypes={
                        (global?.definition as { fileTypes?: string[] } | undefined)
                          ?.fileTypes
                      }
                      uploads={uploadsFor(
                        placementUploadsOf(global, key),
                        detail,
                        key,
                        presented.ref
                      )}
                      disabled={documentDisabled(
                        presented,
                        key,
                        settled,
                        agentEditable,
                        lockedFields
                      )}
                      mutations={mutations}
                      // §Documents: the placement's full read model — extraction, AML, required,
                      // per-file types — so the card renders WM-parity, not just the file list
                      document={global?.document}
                      // §Late-docs-note: on a settled step every offered attach is a late addition — confirm
                      // with an optional note; the notes already anchored to the document render read-only
                      lateAddition={settled}
                      notes={global?.comments}
                      // §Generated-forms: settle pending autosaves before a generate renders the
                      // form — it must print what is on screen, not the last debounced draft
                      flushDraft={async () => {
                        flushAutosave();
                        await autosaveChain.current;
                      }}
                    />
                  );
                }
                const withAppeal = (body: ReactNode): JSX.Element => (
                  <Box key={key}>
                    {body}
                    <InlineAppeal anchor={appealAnchorOf(global, key)} label={presented.l} />
                  </Box>
                );
                if (isUpstreamMirror && !mirrorEditable) {
                  // §Recap: an un-opted mirror is a read-only display — label + value rows
                  return withAppeal(
                    recapRow(key, presented, mirrorRecapValue(detail, field, values, key))
                  );
                }
                if (formReadOnly || presented.mode === "view") {
                  // §Recap: settled / view-only — the design's plain rows, not dimmed inputs
                  return withAppeal(
                    recapRow(
                      key,
                      presented,
                      resolvedFieldValue(values, key, field, isUpstreamMirror, detail, global)
                    )
                  );
                }
                return withAppeal(
                  <FormFieldInput
                    key={key}
                    field={presented}
                    value={
                      resolvedFieldValue(
                        values,
                        key,
                        field,
                        isUpstreamMirror,
                        detail,
                        global
                      ) as never
                    }
                    error={ruleErrors[key]}
                    returnedReason={returnedReasonOf(detail, key)}
                    returnedAttachments={detail.fieldReviews?.[key]?.attachments}
                    onChange={(next) => setValue(key, next)}
                    // settled / view-mode render as recap rows above — only a precondition lock
                    // still shows a disabled input here (temporary state, edit-shaped)
                    disabled={lockedFields.has(key)}
                    hiddenOptions={hiddenOptionsFor(key)}
                    parentValue={dependentParentValue(
                      presented,
                      registrySources,
                      valueBySourceKey
                    )}
                  />
                );
              })}
            </Stack>
          </Box>
        ))}
        {!schema.length && (
          <Typography variant="body2" color={colors.semantic.text.secondary}>
            This step has no form to fill.
          </Typography>
        )}
      </Stack>

      {/* §ChangeUnit (product ask, 2026-08-06, revised): post-approval actions
          (availableWhenApproved — the server's `available` already filtered the rest away) sit
          BOTTOM-RIGHT of the settled card, after its fields. Approve blockers don't apply —
          the step is already approved; only the action's own `requires` gate can dim it. */}
      {settled && agentMayApprove && actions.length > 0 && (
        <Stack
          direction="row"
          spacing={1.5}
          justifyContent="flex-end"
          sx={{ mt: 2, flexWrap: "wrap", rowGap: 1 }}
        >
          {actions.map((action) =>
            withReason(
              action.requiresMet === false
                ? "This action's requirements are not met yet."
                : "",
              <Button
                key={action.key}
                variant="contained"
                disabled={busy || action.requiresMet === false}
                onClick={() =>
                  handleAction({
                    key: action.key,
                    label: action.label,
                    outcome: action.outcome,
                    requiresComment: action.requiresComment,
                    effects: action.effects,
                    confirmMessage: action.confirmMessage,
                  })
                }
                sx={primaryButtonSx(theme)}
              >
                {action.label}
              </Button>
            )
          )}
        </Stack>
      )}

      {/* §Comments: the WM per-step thread — full history + Add a comment while writable.
          §Comment-toggle: a step authored with `uiFlags.commentsEnabled: false` has no comment
          channel — the composer goes (WM 400s the write anyway), the existing thread stays. */}
      {historyView && <StepHistoryPanel key={step.uid} stepUid={step.uid} groupBySourceStep={historyView === "grouped"} />}
      <CommentsPanel
        comments={conversationOf(detail.comments)}
        caseUid={caseUid ?? ""}
        stepUid={step.uid}
        editable={!settled && agentEditable && detail.uiFlags?.commentsEnabled !== false}
        busy={mutations.comment.isLoading}
        onPost={(content, parentUid, attachmentUids) =>
          mutations.comment.mutateAsync({ content, parentUid, attachmentUids })
        }
      />

      {/* read access / not-yet-opened / locked: fields stay visible (disabled), no Save/Submit */}
      {!settled && !agentEditable && agentAccess && (
        <Typography
          variant="caption"
          color={colors.semantic.text.tertiary}
          sx={{ display: "block", mt: 1 }}
        >
          {AGENT_LOCK_MESSAGE[agentAccess.reason ?? "READ_ONLY"] ??
            AGENT_LOCK_MESSAGE.READ_ONLY}
        </Typography>
      )}

      {/* §W3.2: credit-feedback confirmation lives on the cards' own CTAs — no generic Submit */}
      {!settled && agentEditable && !creditFeedback && (
        <>
          {/* §Mobile: Save + the save caption + every engine action share one row, and each
              button sets whiteSpace:nowrap — at 390px a two-verb step (e.g. "Update payment plan"
              + "Escalate") pushed the last button past the card edge. Let the row wrap. */}
          <Stack
            direction="row"
            spacing={1.5}
            useFlexGap
            sx={{ mt: 2, flexWrap: "wrap", rowGap: 1.5, alignItems: "center" }}
          >
            <DraftSaveControls
              mobileValue={values[CLIENT_MOBILE_KEY]}
              savedMobileValue={savedSnapshot[CLIENT_MOBILE_KEY]}
              dirty={dirty}
              saving={mutations.save.isLoading}
              busy={busy}
              onSave={saveDraft}
            />
            {agentMayApprove && (actions.length > 0 ? (
              actions.map((action) =>
                withReason(
                  disabledReasonFor(action),
                  <Button
                    key={action.key}
                    variant={action.outcome === "REJECTED" ? "outlined" : "contained"}
                    disabled={
                      busy ||
                      pending ||
                      action.requiresMet === false ||
                      // §Decision-gate: approve-side buttons (incl. legacy no-outcome, no-key
                      // actions, which complete via the APPROVED path) wait for the owed
                      // reviews/decisions. §MD-bypass: EXCEPT a keyed non-terminal action (no
                      // `outcome` — fires via invoke-action and never completes the step), which
                      // the completion-blockers gate must not touch — e.g. "Request MD bypass"
                      // has to stay pressable on a step whose required answers are still empty.
                      (action.outcome !== "REJECTED" &&
                        approveBlocked &&
                        !actionUsesInvokeEndpoint(action))
                    }
                    // Explicit outcomes complete with their action key. A keyed action without an
                    // outcome is non-terminal and keeps its key for invoke-action.
                    onClick={() =>
                      handleAction({
                        key:
                          action.outcome || actionUsesInvokeEndpoint(action) ? action.key : null,
                        label: action.label,
                        outcome: action.outcome,
                        requiresComment: action.requiresComment,
                        effects: action.effects,
                        confirmMessage: action.confirmMessage,
                      })
                    }
                    sx={
                      action.outcome === "REJECTED"
                        ? destructiveButtonSx(theme)
                        : primaryButtonSx(theme)
                    }
                  >
                    {action.label}
                  </Button>
                )
              )
            ) : (
              withReason(
                pending ? pendingReason : approveBlocked ? blockedReason : "",
                <Button
                  variant="contained"
                  disabled={busy || pending || approveBlocked}
                  onClick={() => handleAction({ key: null })}
                  sx={primaryButtonSx(theme)}
                >
                  Submit
                </Button>
              )
            ))}
          </Stack>
          {pending && (
            <Typography
              variant="caption"
              color={colors.semantic.text.tertiary}
              sx={{ display: "block", mt: 1 }}
            >
              {pendingReason}
            </Typography>
          )}
        </>
      )}

      {/* §Modals: one shell for the credit-feedback paths and every comment-gated action */}
      <ActionConfirmModal
        request={confirm?.request ?? null}
        busy={busy}
        caseUid={caseUid}
        attachmentContext={confirm?.attachmentContext}
        onCancel={() => setConfirm(null)}
        onConfirm={(comment, attachmentUids) => {
          const pendingConfirm = confirm;
          return pendingConfirm?.run(comment, attachmentUids);
        }}
      />
      <DbrSummaryModal
        open={dbrOpen}
        onClose={() => setDbrOpen(false)}
        eligibility={dbrEligibility}
        approvedAmount={approvedAmount}
      />
    </Box>
  );

  /* §Review-appeal: sales can comply with a rejection by fixing it, or challenge it — addressed to the
     reviewer who made that exact decision, who is notified and can reply. The surface is PROVIDED rather
     than passed: an appeal renders beside the item it challenges, and those items sit several levels down
     (a file row in DocumentField, an owner card in TwoSidedDocument, a field in one of FormFieldInput's
     nine shells). Nothing appears until a field is flagged appealable in the Builder and something on the
     step is actually returned. */
  return (
    <AppealsProvider
      stepUid={step.uid}
      caseUid={caseUid ?? ""}
      detail={detail}
      editable={!settled && agentEditable}
    >
      {body}
    </AppealsProvider>
  );
};

export default StepForm;
